
import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Image as ImageIcon, ArrowRight, Check, Wand2, Zap } from 'lucide-react';
import Button from './Button';
import { Product, ImageModel } from '../types';
import { editImageWithPrompt, enhancePrompt } from '../services/geminiService';
import { readFileAsBase64, getBase64Data, getMimeTypeFromBase64 } from '../utils/fileHelpers';

interface EditorProps {
  onSave: (url: string, prompt: string) => void;
  activeProduct: Product | null;
}

const Editor: React.FC<EditorProps> = ({ onSave, activeProduct }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState<ImageModel>(ImageModel.NANO_BANANA);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
      if (activeProduct && activeProduct.images.length > 0) {
        setSelectedImage(activeProduct.images[0].url);
      }
  }, [activeProduct]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const base64 = await readFileAsBase64(file);
        setSelectedImage(base64);
        setResultImage(null);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleEdit = async () => {
    if (!selectedImage || !prompt.trim()) return;

    setIsProcessing(true);
    try {
      const base64Data = getBase64Data(selectedImage);
      const mimeType = getMimeTypeFromBase64(selectedImage);
      const result = await editImageWithPrompt(base64Data, prompt, mimeType, model);
      setResultImage(result);
    } catch (error) {
      alert("Failed to edit image.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEnhance = async () => {
    if (!prompt.trim()) return;
    setIsEnhancing(true);
    try {
      const enhanced = await enhancePrompt(prompt);
      setPrompt(enhanced);
    } catch (error) {
      console.error(error);
    } finally {
      setIsEnhancing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto pb-12">
       <div className="mb-10 flex justify-between items-end">
        <div>
          <h2 className="text-5xl font-serif font-bold text-stone-900 mb-4 tracking-tight">Magic Editor</h2>
          <p className="text-xl text-stone-500 font-light max-w-2xl leading-relaxed">
            {activeProduct ? <span>Retouching assets for <span className="font-semibold text-stone-900">{activeProduct.name}</span></span> : "Use natural language to retouch, filter, or modify your jewelry images."}
          </p>
        </div>
        <div className="flex items-center gap-2 bg-stone-100 p-1 rounded-lg">
           <button 
             onClick={() => setModel(ImageModel.NANO_BANANA)}
             className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${model === ImageModel.NANO_BANANA ? 'bg-white shadow-sm text-stone-900' : 'text-stone-400 hover:text-stone-600'}`}
           >
             Nano Banana
           </button>
           <button 
             onClick={() => setModel(ImageModel.NANO_BANANA_PRO)}
             className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all flex items-center ${model === ImageModel.NANO_BANANA_PRO ? 'bg-white shadow-sm text-stone-900' : 'text-stone-400 hover:text-stone-600'}`}
           >
             Nano Banana Pro
             <Zap className="w-3 h-3 ml-1 fill-amber-400 text-amber-500" />
           </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 lg:h-[650px]">
        {/* Left: Input */}
        <div className="flex flex-col h-[500px] lg:h-full space-y-6">
          <div className="flex-1 bg-white rounded-[2rem] overflow-hidden relative border border-stone-100 shadow-xl shadow-stone-200/40 group">
             {selectedImage ? (
                <div className="w-full h-full relative p-8 flex items-center justify-center bg-stone-50/30">
                  <img src={selectedImage} alt="Original" className="max-w-full max-h-full object-contain" />
                  <div className="absolute top-6 right-6">
                    <Button variant="glass" size="sm" onClick={() => fileInputRef.current?.click()} className="shadow-lg">
                       Change Source
                    </Button>
                  </div>
                </div>
             ) : (
                <div 
                  className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-stone-50 transition-colors bg-stone-50/20"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                     <ImageIcon className="w-10 h-10 text-stone-300 group-hover:text-stone-500" />
                  </div>
                  <p className="text-lg font-bold text-stone-900">Upload Base Image</p>
                  <p className="text-sm text-stone-400 mt-1">Click to browse your files</p>
                </div>
             )}
             <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
          </div>

          <div className="bg-white p-6 rounded-[2rem] border border-stone-100 shadow-xl shadow-stone-200/50">
             <div className="flex justify-between items-center mb-3 px-1">
                <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Magic Instruction</label>
                <button 
                  onClick={handleEnhance} 
                  disabled={!prompt.trim() || isEnhancing}
                  className="text-xs flex items-center text-purple-600 hover:text-purple-700 font-bold uppercase tracking-wider disabled:opacity-50 transition-colors"
               >
                  <Wand2 className={`w-3 h-3 mr-1.5 ${isEnhancing ? 'animate-spin' : ''}`} />
                  {isEnhancing ? 'Enhancing...' : 'Enhance Prompt'}
               </button>
             </div>
             <div className="flex gap-4">
               <input 
                 type="text" 
                 value={prompt}
                 onChange={(e) => setPrompt(e.target.value)}
                 placeholder='e.g., "Add a retro filter", "Make the diamond sparkle more"'
                 className="flex-1 p-4 bg-stone-50 border border-stone-200 rounded-2xl focus:ring-2 focus:ring-stone-900 focus:outline-none placeholder:text-stone-400 text-stone-900 shadow-inner"
               />
               <Button onClick={handleEdit} disabled={!selectedImage || !prompt || isProcessing} isLoading={isProcessing} className="px-6 shadow-lg shadow-stone-900/10" size="lg">
                 <Sparkles className="w-5 h-5" />
               </Button>
             </div>
          </div>
        </div>

        {/* Right: Output */}
        <div className="flex flex-col h-[500px] lg:h-full bg-[#1C1C1E] rounded-[2.5rem] relative overflow-hidden shadow-2xl shadow-stone-900/30 border border-stone-800">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-stone-800/50 via-stone-950 to-black opacity-60"></div>
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay"></div>
          
          {resultImage ? (
            <div className="w-full h-full flex flex-col relative z-10">
              <div className="flex-1 p-8 lg:p-10 flex items-center justify-center min-h-0">
                 <img src={resultImage} alt="Result" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl ring-1 ring-white/10 animate-in zoom-in-95 duration-700" />
              </div>
              <div className="bg-white/5 backdrop-blur-xl border-t border-white/5 p-6 lg:p-8 flex justify-between items-center gap-6 rounded-b-[2.5rem] mx-2 mb-2">
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-white text-sm lg:text-base mb-1">Edited Result</p>
                    <p className="text-xs text-stone-400 truncate font-medium">{prompt}</p>
                </div>
                <Button variant="white" size="md" onClick={() => onSave(resultImage, prompt)} className="flex-shrink-0 whitespace-nowrap shadow-lg">
                  <Check className="w-4 h-4 mr-2" />
                  Save Asset
                </Button>
              </div>
            </div>
          ) : (
             <div className="w-full h-full flex items-center justify-center text-stone-600 relative z-10">
               <div className="text-center p-8">
                 <div className="w-24 h-24 border border-stone-700/50 rounded-full flex items-center justify-center mx-auto mb-8 bg-stone-800/50 backdrop-blur-sm shadow-xl">
                    <ArrowRight className="w-10 h-10 text-stone-600" />
                 </div>
                 <h3 className="text-2xl font-serif text-stone-300 mb-3">Ready to Transform</h3>
                 <p className="text-stone-500 max-w-xs mx-auto text-sm leading-relaxed">
                    Enter your instruction on the left to see the AI editing magic happen here.
                 </p>
               </div>
             </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Editor;
